import AssistantComposer from "@/components/vistassistant/AssistantComposer";
import AssistantEmptyState from "@/components/vistassistant/AssistantEmptyState";
import AssistantHeader from "@/components/vistassistant/AssistantHeader";
import ContextGauge from "@/components/vistassistant/ContextGauge";
import ConversationFullCard from "@/components/vistassistant/ConversationFullCard";
import ConversationHistory from "@/components/vistassistant/ConversationHistory";
import MessageList from "@/components/vistassistant/MessageList";
import QuestionOutline, {
    QuestionOutlineMenu,
} from "@/components/vistassistant/QuestionOutline";
import AssistantSidePanel from "@/components/vistassistant/sidePanel/AssistantSidePanel";
import useSidePanes from "@/components/vistassistant/sidePanel/useSidePanes";
import { VistassistantMailContext } from "@/contexts/VistassistantMailContext";
import { MailsListId } from "@/domain/entities/emails/Email";
import AIResponse, {
    ContentAction,
} from "@/domain/entities/vistassistant/AIResponse";
import type { PendingTicketFormData } from "@/domain/entities/vistassistant/TicketTypes";
import { TicketType } from "@/domain/entities/vistassistant/TicketTypes";
import UserRequest from "@/domain/entities/vistassistant/UserRequest";
import useUnreadAnswer from "@/hooks/assistant/useUnreadAnswer";
import {
    assistantMails,
    type AssistantMailsCache,
} from "@/hooks/mails/assistantMailsCache";
import useMailActions from "@/hooks/useMailActions";
import useVistassistant from "@/hooks/useVistassistant";
import useMatchesMedia from "@/hooks/utils/useMatchesMedia";
import useStableCallback from "@/hooks/utils/useStableCallback";
import { queryKeys } from "@/lib/queryKeys";
import { cn } from "@/lib/utils";
import assistantStore, {
    clearPendingPrefill,
    clearPendingQuery,
    closeAssistant,
} from "@/stores/assistantStore";
import { useConversation } from "@cs_teamwaydev/mailvista-sdk_react";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "@tanstack/react-store";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Under Tailwind's `md` breakpoint, the docked panels have no room: they cover the chat (`max-md:`).
const NARROW_SCREEN_QUERY = "(max-width: 767px)";

const Vistassistant = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    // What is opened from the chat, in the side panel: one group per kind, each with its tabs.
    const { panes, openers, paneActions } = useSidePanes();
    const [actionDoneRequestIds, setActionDoneRequestIds] = useState<
        Set<string>
    >(() => new Set());
    const pendingQuery = useSelector(
        assistantStore,
        (s: { pendingQuery?: string }) => s.pendingQuery,
    );
    const pendingPrefill = useSelector(
        assistantStore,
        (s: { pendingPrefill?: string }) => s.pendingPrefill,
    );
    const isAssistantOpen = useSelector(assistantStore, (s) => s.isOpen);
    const {
        sendMessage,
        retryLastMessage,
        stopAnswer,
        answerFeedback,
        sendAnswerFeedback,
        openConversation,
        isSendingMessage,
        messages,
        isWaitingForDeepAnalysis,
        resetConversation,
        handleMoreDataCallBack,
        isLoadingMoreData,
        submitTicket,
        isConversationLimitReached,
        hasConversationMemory,
        contextSize,
        usage,
        canCompactConversation,
        compactConversation,
        pendingReasoning,
    } = useVistassistant();

    const handleSubmitTicket = async (
        data: PendingTicketFormData,
        ticketType: TicketType,
    ) => {
        await submitTicket(data, ticketType);
    };

    const hasSubmittedRef = useRef(false);
    const conversation = useConversation();
    const isNarrowScreen = useMatchesMedia(NARROW_SCREEN_QUERY);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const isHistoryShown =
        isAssistantOpen &&
        hasConversationMemory &&
        (!isNarrowScreen || isHistoryOpen);
    // « Réponse à lire »: arrived while its thread was not on screen (assistant closed, tab hidden, or the
    // history over the thread on a narrow screen), shown on the assistant's button and in the history.
    const unreadAnswer = useUnreadAnswer(
        isSendingMessage,
        isHistoryShown && isNarrowScreen,
    );

    const [inputValue, setInputValue] = useState("");

    const {
        setReadStatusBulk,
        setProcessStatusBulk,
        setMailFocusBulk,
        deleteMailsBulk,
    } = useMailActions();

    const getMessageEmails = (userRequestId: string) =>
        assistantMails(
            queryClient.getQueryData<AssistantMailsCache>(
                queryKeys.mails.list(userRequestId),
            ),
        );

    const runBulkAction = async (message: AIResponse) => {
        const action = message.content.contentAction;
        if (!action) return;

        const listId = message.userRequestId as MailsListId;
        const mails = getMessageEmails(message.userRequestId);
        if (mails.length === 0) return;

        try {
            let succeeded = false;
            switch (action) {
                case ContentAction.DELETE:
                    succeeded = await deleteMailsBulk.mutateAsync({
                        mails,
                        listId,
                    });
                    break;
                case ContentAction.MARKASREAD:
                    succeeded = await setReadStatusBulk.mutateAsync({
                        mails,
                        readStatus: true,
                        listId,
                    });
                    break;
                case ContentAction.MARKASUNREAD:
                    succeeded = await setReadStatusBulk.mutateAsync({
                        mails,
                        readStatus: false,
                        listId,
                    });
                    break;
                case ContentAction.MARKASFOCUS:
                    succeeded = await setMailFocusBulk.mutateAsync({
                        mails,
                        isFocus: true,
                        listId,
                    });
                    break;
                case ContentAction.MARKASUNFOCUS:
                    succeeded = await setMailFocusBulk.mutateAsync({
                        mails,
                        isFocus: false,
                        listId,
                    });
                    break;
                case ContentAction.MARKASUSERPROCESSED:
                    succeeded = await setProcessStatusBulk.mutateAsync({
                        mails,
                        isProcessed: true,
                        listId,
                    });
                    break;
                case ContentAction.MARKASUSERUNPROCESSED:
                    succeeded = await setProcessStatusBulk.mutateAsync({
                        mails,
                        isProcessed: false,
                        listId,
                    });
                    break;
                default:
                    return;
            }
            if (succeeded) {
                setActionDoneRequestIds((prev) =>
                    new Set(prev).add(message.userRequestId),
                );
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleSubmitInput = async (
        e: FormEvent<HTMLFormElement>,
        queryValue?: string,
    ) => {
        e.preventDefault();
        if (!inputValue.trim() && !queryValue) return;
        if (hasSubmittedRef.current) return; // prevents multiple submits on re-render

        hasSubmittedRef.current = true;

        const outcome = await sendMessage(inputValue || (queryValue as string));
        // Stopped (« Arrêter »): the question stays in the bar, to be changed or sent again.
        if (outcome !== "stopped") setInputValue("");

        hasSubmittedRef.current = false;
    };

    useEffect(() => {
        if (pendingQuery) {
            clearPendingQuery();
            setInputValue(pendingQuery);
            const formEvent = new Event(
                "submit",
            ) as unknown as FormEvent<HTMLFormElement>;
            handleSubmitInput(formEvent, pendingQuery);
        }
    }, [pendingQuery]);

    useEffect(() => {
        if (pendingPrefill !== undefined) {
            clearPendingPrefill();
            setInputValue(pendingPrefill);
        }
    }, [pendingPrefill]);

    useEffect(
        () => () => {
            resetConversation.mutate();
        },
        [],
    );

    // The assistant stays mounted (only CSS-hidden) when closed: the side panel is not rendered
    // then, so a mail thread stops fetching (it would race the main view's thread query); its tabs
    // are kept for when the assistant opens again.
    useEffect(() => {
        if (!isAssistantOpen) setIsHistoryOpen(false);
    }, [isAssistantOpen]);

    // « Autre… » under a question: the cursor goes to the input.
    const [focusRequest, setFocusRequest] = useState(0);
    // A choice of a question, clicked: sent as the next message, unless a turn is already running.
    const answerQuestion = useRef<(reply: string) => void>(() => undefined);
    answerQuestion.current = (reply: string) => {
        if (!isSendingMessage) void sendMessage(reply);
    };

    const vistassistantMailContextValue = useMemo(
        () => ({
            isVistassistantResult: true,
            ...openers,
            onAnswerQuestion: (reply: string) => answerQuestion.current(reply),
            onFocusInput: () => setFocusRequest((count) => count + 1),
        }),
        [openers],
    );

    const leaveConversation = () => {
        setActionDoneRequestIds(new Set());
    };

    const startNewConversation = () => {
        resetConversation.mutate();
        leaveConversation();
    };

    // The history is docked beside the chat: picking a conversation leaves it open. On a narrow
    // screen it covers the chat instead, so a pick closes it.
    const closeHistoryIfCovering = () => {
        if (isNarrowScreen) {
            setIsHistoryOpen(false);
        }
    };

    // A past conversation replaces the one shown; the next question goes on from it.
    const openPastConversation = async (id: string) => {
        const opened = await openConversation(id);
        if (opened) leaveConversation();

        return opened;
    };

    const isEmpty = messages.length === 0;
    const questions = useMemo(
        () =>
            messages.flatMap((message, index) =>
                message instanceof UserRequest
                    ? [{ index, text: message.content }]
                    : [],
            ),
        [messages],
    );
    const compact = () => compactConversation.mutate();

    // Stable identities for the handlers given to the thread and the history, both protected by
    // React.memo: typing in the input no longer re-renders them (audit de performance du 15/10, P4-02).
    const stable = {
        handleMoreDataCallBack: useStableCallback(handleMoreDataCallBack),
        runBulkAction: useStableCallback(runBulkAction),
        sendAnswerFeedback: useStableCallback(sendAnswerFeedback),
        retryLastMessage: useStableCallback(retryLastMessage),
        handleSubmitTicket: useStableCallback(handleSubmitTicket),
        closeHistoryIfCovering: useStableCallback(closeHistoryIfCovering),
        hideHistory: useStableCallback(() => setIsHistoryOpen(false)),
        startNewConversation: useStableCallback(startNewConversation),
        openPastConversation: useStableCallback(openPastConversation),
    };

    return (
        <div className="assistant-backdrop relative flex h-full w-full flex-col overflow-hidden font-body">
            <div className="relative flex min-h-0 flex-1">
                {isHistoryShown && (
                    <ConversationHistory
                        currentId={conversation.getConversationId()}
                        runningId={
                            isSendingMessage
                                ? conversation.getConversationId()
                                : undefined
                        }
                        unreadId={
                            unreadAnswer
                                ? conversation.getConversationId()
                                : undefined
                        }
                        onClose={stable.closeHistoryIfCovering}
                        isCovering={isNarrowScreen}
                        onCloseAssistant={closeAssistant}
                        onHide={stable.hideHistory}
                        onCurrentDeleted={stable.startNewConversation}
                        onNewConversation={stable.startNewConversation}
                        onOpen={stable.openPastConversation}
                    />
                )}
                <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
                    <AssistantHeader
                        canStartNew={!isEmpty && !isHistoryShown}
                        isResetting={resetConversation.isPending}
                        onClose={closeAssistant}
                        onNewConversation={startNewConversation}
                        outline={<QuestionOutlineMenu questions={questions} />}
                        onShowHistory={
                            hasConversationMemory && !isHistoryShown
                                ? () => setIsHistoryOpen(true)
                                : undefined
                        }
                    />
                    {/* The questions, as chapters, at the top left of the thread. */}
                    <div className="relative top-11 h-0">
                        <QuestionOutline questions={questions} />
                    </div>
                    {/* The input bar keeps its place whether the thread is empty or not: what is
                    being typed survives the first answer. */}
                    <div
                        className={cn(
                            "flex min-h-0 min-w-0 flex-1 flex-col",
                            isEmpty &&
                                "gap-5 overflow-y-auto py-14 [justify-content:safe_center]",
                        )}
                    >
                        <VistassistantMailContext.Provider
                            value={vistassistantMailContextValue}
                        >
                            {isEmpty ? (
                                <AssistantEmptyState />
                            ) : (
                                <MessageList
                                    actionDoneRequestIds={actionDoneRequestIds}
                                    className="min-h-0 w-full flex-1"
                                    handleMoreDataCallBack={
                                        stable.handleMoreDataCallBack
                                    }
                                    isLoadingMoreData={isLoadingMoreData}
                                    isThinking={isSendingMessage}
                                    messages={messages}
                                    onBulkAction={stable.runBulkAction}
                                    answerFeedback={answerFeedback}
                                    onAnswerFeedback={stable.sendAnswerFeedback}
                                    onRetry={stable.retryLastMessage}
                                    pendingReasoning={pendingReasoning}
                                    onConfirmCreateFromSearch={
                                        stable.handleSubmitTicket
                                    }
                                    onSubmitTicket={stable.handleSubmitTicket}
                                />
                            )}
                        </VistassistantMailContext.Provider>
                        <div className="flex flex-none justify-center px-5 pb-4 pt-2">
                            <div className="w-full max-w-[760px]">
                                {isConversationLimitReached ? (
                                    <ConversationFullCard
                                        canCompact={canCompactConversation}
                                        hasConversationMemory={
                                            hasConversationMemory
                                        }
                                        isCompacting={
                                            compactConversation.isPending
                                        }
                                        isResetting={
                                            resetConversation.isPending
                                        }
                                        onCompact={compact}
                                        onNewConversation={startNewConversation}
                                    />
                                ) : (
                                    <AssistantComposer
                                        contextGauge={
                                            hasConversationMemory ? (
                                                <ContextGauge
                                                    canCompact={
                                                        canCompactConversation
                                                    }
                                                    isCompacting={
                                                        compactConversation.isPending
                                                    }
                                                    onCompact={compact}
                                                    onNewConversation={
                                                        startNewConversation
                                                    }
                                                    size={contextSize}
                                                    usage={usage}
                                                />
                                            ) : undefined
                                        }
                                        handleSubmitInput={handleSubmitInput}
                                        inputValue={inputValue}
                                        isLoadingMoreData={isLoadingMoreData}
                                        isSendingMessage={isSendingMessage}
                                        onStop={stopAnswer}
                                        isWaitingForDeepAnalysis={
                                            isWaitingForDeepAnalysis
                                        }
                                        focusRequest={focusRequest}
                                        placeholder={t(
                                            "vistassistant.placeholder",
                                        )}
                                        setInputValue={setInputValue}
                                        showSuggestionGrid={isEmpty}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {isAssistantOpen && (
                    <AssistantSidePanel panes={panes} {...paneActions} />
                )}
            </div>
        </div>
    );
};

export default Vistassistant;
